import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Coins,
  Trophy,
  BookOpen,
  Zap,
  TrendingUp,
  ChevronRight,
  Spade,
  Play as PlayIcon,
  BarChart3,
  Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/features/auth/auth-store";
import { usePokerStore } from "@/features/poker/poker-store";
import { apiGetMe, apiClaimAdReward } from "@/features/auth/api";
import { fetchXp } from "@/features/learn/api";
import { useXpStore } from "@/features/learn/xp-store";
import { Flame, RotateCcw, Star } from "lucide-react";
import { showRewardedAd } from "@/lib/admob";
import { toast } from "sonner";

const AD_COOLDOWN_MS = 30 * 60 * 1000; // 30 minutes, matches backend

const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const updateUser = useAuthStore((s) => s.updateUser);

  const pokerRoom = usePokerStore((s) => s.room);
  const pokerToken = usePokerStore((s) => s.token);
  const hasActiveSession = pokerRoom && pokerToken;

  const [stats, setStats] = useState({ handsPlayed: 0, handsWon: 0 });
  const [adLoading, setAdLoading] = useState(false);
  const [adCooldownSecs, setAdCooldownSecs] = useState(0);

  // Initialise cooldown from localStorage and tick it down
  useEffect(() => {
    const stored = localStorage.getItem("adLastClaim");
    if (!stored) return;
    const elapsed = Date.now() - Number(stored);
    const remaining = Math.max(0, Math.ceil((AD_COOLDOWN_MS - elapsed) / 1000));
    if (remaining === 0) return;
    setAdCooldownSecs(remaining);
    const interval = setInterval(() => {
      setAdCooldownSecs((s) => {
        if (s <= 1) { clearInterval(interval); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const xp = useXpStore();
  const setXp = useXpStore((s) => s.setXp);

  useEffect(() => {
    if (!token) return;
    apiGetMe(token)
      .then((data) => {
        updateUser({ chips: data.chips });
        setStats({ handsPlayed: data.handsPlayed, handsWon: data.handsWon });
      })
      .catch(() => {});
    fetchXp(token)
      .then((data) => setXp(data))
      .catch(() => {});
  }, [token, setXp, updateUser]);

  const winRate =
    stats.handsPlayed > 0
      ? Math.round((stats.handsWon / stats.handsPlayed) * 100)
      : 0;

  const handleWatchAd = async () => {
    if (!token) return;
    setAdLoading(true);
    try {
      const rewarded = await showRewardedAd();
      if (!rewarded) {
        toast.error("El anuncio no pudo cargarse. Inténtalo de nuevo.");
        return;
      }
      const result = await apiClaimAdReward(token);
      updateUser({ chips: (user?.chips ?? 0) + result.chips_awarded });
      toast.success(`+${result.chips_awarded} fichas ganadas`);
      localStorage.setItem("adLastClaim", String(Date.now()));
      const totalSecs = AD_COOLDOWN_MS / 1000;
      setAdCooldownSecs(totalSecs);
      const interval = setInterval(() => {
        setAdCooldownSecs((s) => {
          if (s <= 1) { clearInterval(interval); return 0; }
          return s - 1;
        });
      }, 1000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Error al reclamar el premio.";
      toast.error(msg);
    } finally {
      setAdLoading(false);
    }
  };

  const formatChips = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 10_000) return `${Math.round(n / 1000)}k`;
    if (n >= 1_000) return `${(n / 1000).toFixed(1)}k`;
    return n.toLocaleString();
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="p-4 space-y-5 max-w-screen-xl mx-auto">
        {/* Greeting */}
        <div className="pt-1 animate-fade-in-up">
          <h1 className="text-2xl font-bold tracking-tight">
            {t("home_greeting", { name: user?.username ?? "Player" })}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t("home_subtitle")}
          </p>
        </div>

        {/* Active session */}
        {hasActiveSession && (
          <div className="glass border-secondary/30 p-4 animate-fade-in-up">
            <div className="flex items-center gap-3">
              <div className="bg-secondary/20 w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
                <PlayIcon className="w-5 h-5 text-secondary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm">
                  {t("home_active_game")}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {t("home_active_game_desc")}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => navigate("/table")}
                className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shrink-0"
              >
                {t("return_to_table")}
              </Button>
            </div>
          </div>
        )}

        {/* Quick Play Cards */}
        <div
          className="grid grid-cols-2 gap-3 animate-fade-in-up"
          style={{ animationDelay: "75ms" }}
        >
          <button
            onClick={() => navigate("/play?tab=cash")}
            className="glass hover-lift p-4 text-left group relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative">
              <div className="bg-green-500/20 w-10 h-10 rounded-xl flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
                <Coins className="w-5 h-5 text-green-400" />
              </div>
              <p className="font-bold text-sm">Cash Game</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {t("home_quick_play")}
              </p>
            </div>
          </button>

          <button
            onClick={() => navigate("/play?tab=sng")}
            className="glass hover-lift p-4 text-left group relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-secondary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative">
              <div className="bg-secondary/20 w-10 h-10 rounded-xl flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
                <Trophy className="w-5 h-5 text-secondary" />
              </div>
              <p className="font-bold text-sm">Sit & Go</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {t("home_quick_play")}
              </p>
            </div>
          </button>
        </div>

        {/* Stats Row */}
        <div
          className="grid grid-cols-3 gap-3 animate-fade-in-up"
          style={{ animationDelay: "150ms" }}
        >
          <div className="glass p-3 text-center">
            <Coins className="w-4 h-4 text-secondary mx-auto mb-1.5" />
            <p className="text-lg font-bold text-secondary leading-tight">
              {user?.chips != null ? formatChips(user.chips) : "---"}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {t("home_chips")}
            </p>
          </div>
          <div className="glass p-3 text-center">
            <TrendingUp className="w-4 h-4 text-primary mx-auto mb-1.5" />
            <p className="text-lg font-bold text-primary leading-tight">
              {winRate}%
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {t("home_win_rate")}
            </p>
          </div>
          <div className="glass p-3 text-center">
            <Spade className="w-4 h-4 text-muted-foreground mx-auto mb-1.5" />
            <p className="text-lg font-bold leading-tight">
              {stats.handsPlayed}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {t("home_hands")}
            </p>
          </div>
        </div>

        {/* Streak + XP + Reviews */}
        {xp.loaded && (
          <div
            className="glass p-4 animate-fade-in-up"
            style={{ animationDelay: "175ms" }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Streak */}
                <div className="flex items-center gap-1.5">
                  <Flame className={`w-5 h-5 ${xp.currentStreak > 0 ? "text-orange-400" : "text-muted-foreground"}`} />
                  <span className={`text-lg font-bold ${xp.currentStreak > 0 ? "text-orange-400" : "text-muted-foreground"}`}>
                    {xp.currentStreak}
                  </span>
                </div>
                {/* XP */}
                <div className="flex items-center gap-1.5">
                  <Star className="w-4 h-4 text-secondary" />
                  <span className="text-sm font-bold text-secondary">
                    {xp.totalXp.toLocaleString()} XP
                  </span>
                </div>
              </div>
              {/* Reviews due */}
              {xp.reviewsDue > 0 && (
                <button
                  onClick={() => navigate("/learn/review")}
                  className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  {xp.reviewsDue} {t("home_reviews_due", "reviews")}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Watch Ad for Chips */}
        <button
          onClick={handleWatchAd}
          disabled={adLoading || adCooldownSecs > 0}
          className="glass hover-lift w-full p-4 text-left group animate-fade-in-up disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ animationDelay: "200ms" }}
        >
          <div className="flex items-center gap-3">
            <div className="bg-yellow-500/15 w-11 h-11 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-yellow-500/25 transition-colors">
              <Video className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">{t("play_watch_ad")}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {adCooldownSecs > 0
                  ? `${t("play_get_chips")} · ${Math.floor(adCooldownSecs / 60)}:${String(adCooldownSecs % 60).padStart(2, "0")}`
                  : t("play_get_chips")}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </button>

        {/* Continue Learning */}
        <button
          onClick={() => navigate("/learn")}
          className="glass hover-lift w-full p-4 text-left group animate-fade-in-up"
          style={{ animationDelay: "225ms" }}
        >
          <div className="flex items-center gap-3">
            <div className="bg-primary/15 w-11 h-11 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-primary/25 transition-colors">
              <BookOpen className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">
                {t("home_continue_learning")}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {t("home_academy_desc")}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </button>

        {/* Daily Challenge */}
        <button
          onClick={() => navigate("/minigames/preflop")}
          className="glass hover-lift w-full p-4 text-left group animate-fade-in-up"
          style={{ animationDelay: "300ms" }}
        >
          <div className="flex items-center gap-3">
            <div className="bg-accent/15 w-11 h-11 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-accent/25 transition-colors">
              <Zap className="w-5 h-5 text-accent" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">
                {t("home_daily_challenge")}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {t("home_daily_desc")}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </button>

        {/* Hand Review */}
        <button
          onClick={() => navigate("/hand-review")}
          className="glass hover-lift w-full p-4 text-left group animate-fade-in-up"
          style={{ animationDelay: "375ms" }}
        >
          <div className="flex items-center gap-3">
            <div className="bg-blue-500/15 w-11 h-11 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-blue-500/25 transition-colors">
              <BarChart3 className="w-5 h-5 text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">
                {t("hand_review_title")}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {t("hand_review_subtitle")}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </button>
      </div>
    </div>
  );
};

export default Home;
